import React, { useCallback } from "react";
import { format } from "date-fns";
import { FLAT_RATE_PLANS, flatRatePlanNames } from "@common/flatRateBilling";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { useDowngradeMutation } from "@api/flatRateBilling.api";
import { useScope } from "@state/scopeContext.lib";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Button } from "@design-system/Button/Button";
import { StandardModalBody } from "@design-system/Modal/Modal";
import {
    BusinessIcon,
    ChatIcon,
    CategoryIcon,
    LedgerIcon,
    SpreadsheetIcon,
} from "@icons";
import { DowngradeFeature } from "./lib";
import styles from "./DowngradeModal.module.scss";
import { PlanFeature } from "./PlanFeature";

interface Props {
    downgradingToPlan: FLAT_RATE_PLANS;
    onHide(): void;
}

const loosingFeaturesDowngradingToBasic: DowngradeFeature[] = [
    { label: "Balance sheet", icon: <SpreadsheetIcon /> },
    { label: "General ledger", icon: <LedgerIcon /> },
    { label: "Custom categories", icon: <CategoryIcon /> },
    { label: "Priority support", icon: <ChatIcon /> },
] as const;

const loosingFeaturesDowngradingToFree = [
    { label: "Multiple entities", icon: <BusinessIcon /> },
    ...loosingFeaturesDowngradingToBasic,
];

export const DowngradeModalContent: React.FC<Props> = ({
    downgradingToPlan,
    onHide,
}) => {
    const { activeWorkspaceKey } = useScope();
    const { toast } = useToaster();
    const { currentPlan, subscriptionDetails, isTrialing } = useBillingStatus();

    const onDowngraded = useCallback(() => {
        if (isTrialing) {
            if (downgradingToPlan === FLAT_RATE_PLANS.FREE) {
                toast(
                    `Plan updated. You still have access to ${flatRatePlanNames[currentPlan]} until the end of your trial.`,
                );
            } else {
                toast(`Plan updated`);
            }
        } else {
            toast(
                `Plan updated. You still have access to ${flatRatePlanNames[currentPlan]} until ${format(subscriptionDetails!.periodEndsAt!, fullDateFullYearFormat)}`,
            );
        }

        onHide();
    }, [
        currentPlan,
        downgradingToPlan,
        subscriptionDetails,
        isTrialing,
        onHide,
        toast,
    ]);
    const downgradeMutation = useDowngradeMutation(
        activeWorkspaceKey,
        onDowngraded,
    );

    let subheading: React.ReactNode;

    const endDateText = format(
        subscriptionDetails!.periodEndsAt!,
        fullDateFullYearFormat,
    );

    if (!isTrialing || downgradingToPlan === FLAT_RATE_PLANS.FREE) {
        subheading = (
            <p>
                You'll still have access to these features until {endDateText}.
            </p>
        );
    } else if (downgradingToPlan === FLAT_RATE_PLANS.BASIC) {
        subheading = <p>You'll lose access to these features.</p>;
    }

    return (
        <StandardModalBody>
            <header className={styles.header}>
                <span className={styles.headline}>
                    Are you sure you want to downgrade to{" "}
                    {flatRatePlanNames[downgradingToPlan]}?
                </span>

                {subheading}
            </header>

            <main className={styles.body}>
                <section className={styles.planFeatures}>
                    {(downgradingToPlan === FLAT_RATE_PLANS.FREE
                        ? loosingFeaturesDowngradingToFree
                        : loosingFeaturesDowngradingToBasic
                    ).map((feature) => (
                        <PlanFeature key={feature.label} feature={feature} />
                    ))}
                </section>

                <section className={styles.team}>
                    <img src="/images/team.png" alt="Echo, Andrew, Bridget" />{" "}
                    If you have any questions, please reach out to our team
                </section>

                <footer className={styles.actions}>
                    <Button
                        variant="secondary"
                        size="xl"
                        onClick={onHide}
                        disabled={downgradeMutation.isPending}
                    >
                        Back
                    </Button>
                    <ButtonWithLoader
                        variant="primary"
                        size="xl"
                        onClick={() =>
                            downgradeMutation.mutate({
                                plan: downgradingToPlan,
                            })
                        }
                        loading={downgradeMutation.isPending}
                    >
                        Yes, downgrade to {flatRatePlanNames[downgradingToPlan]}
                    </ButtonWithLoader>
                </footer>
            </main>
        </StandardModalBody>
    );
};
